import { Component } from 'react';
import type { CategoryDetailsPageQuery } from '@graphql/pages/category-details.generated';
import type { Locale } from '@lib/locale';
import styled from 'styled-components';
import type { PageInjectedProps } from '../Pages/Page';
import MaxWidthLayout from '../../../templates/MaxWidthLayout';
import { BannerBackgroundImgDiv, BannerBackgroundImg } from '../shared/BannerBackgroundImgDiv';
import {
  makeExternalUrl,
  sanitizeHash,
} from '../../../../lib/utils';
import MediaQueryHelper from '../../../templates/MediaQueryHelper';
import { removeShopById, saveShop, removeOfferById, saveOffer } from '../../../../lib/user';
import {
  CATEGORY_PAGE_OFFERS,
  CATEGORY_PAGE_SHOPS,
  GRID,
  OFFERS,
  SHOPS,
} from '../../../../lib/constants';
import ShopCard from '../ShopCard/ShopCard';
import LayoutFilterableCollectionView from '../LayoutCollectionView/LayoutFilterableCollectionView';
import RenderLayoutViews from '../shared/RenderLayoutViews';
import LayoutCollectionView from '../LayoutCollectionView/LayoutCollectionView';
import OfferCard from '../Offer/OfferCard';
import TabGroup from '../shared/TabGroup';

type Category = NonNullable<NonNullable<CategoryDetailsPageQuery['category']>[number]>;
type CategoryConfig = NonNullable<CategoryDetailsPageQuery['globalCategoryConfig']>;
type Props = Partial<Category & CategoryConfig> & {
  locale: Locale;
} & Partial<Pick<PageInjectedProps, 'userData' | 'pageStateModifiers'>>;
type State = { activeTab: string };

const PAGE_CONTEXT = 'category-page';

const CategoryBannerBackgroundImgDiv = styled(BannerBackgroundImgDiv)<{ hasBottomMargin?: number }>`
  max-width: none;
  width: 100%;

  @media screen and (min-width: ${(p) => p.theme.minWidthBreakpoint.desktop}px) {
    ${({ hasBottomMargin }) => hasBottomMargin && `
      margin-bottom: 48px;
    `}
  }
`;

const CategoryBannerBackgroundImg = styled(BannerBackgroundImg)`
  max-width: none;
  width: 100%;
  position: absolute;
  object-fit: cover;
`;

const CenteredColumnDiv = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const TextOverlayH1 = styled.h1`
  ${({ theme }) => theme.typographyRebrand.heading['3xl']}
  z-index: ${(p) => p.theme.elevation[0]};
  color: ${(p) => p.theme.c.text.primary};
  margin: 0 ${(p) => p.theme.unit(5)};
  text-align: center;
  text-transform: uppercase;
`;

const CategoryMobileBannerBackgroundImgDiv = styled(CategoryBannerBackgroundImgDiv)`
  height: ${(p) => p.theme.unit(32)};
`;

const CategoryMobileBannerBackgroundImg = styled(CategoryBannerBackgroundImg)`
  height: ${(p) => p.theme.unit(32)};
`;

const TABS = [CATEGORY_PAGE_SHOPS, CATEGORY_PAGE_OFFERS];

class CategoryPage extends Component<Props, State> {
  categoryData: Record<string, string | null | undefined>;

  subCategoriesNames: string[];

  constructor(props: Props) {
    super(props);

    const {
      subCategories = [],
    } = props;

    this.state = {
      activeTab: '',
    };

    this.categoryData = {};

    this.subCategoriesNames = (subCategories ?? []).map((subcat) => {
      if (subcat && subcat.name && subcat.name.length > 0) {
        return subcat.name.trim();
      }
      return '';
    });

    this.setActiveTab = this.setActiveTab.bind(this);
    this.changeTabBasedOnHash = this.changeTabBasedOnHash.bind(this);
  }

  componentDidMount() {
    const {
      storesTabText,
      offersTabText,
    } = this.props;

    this.categoryData[CATEGORY_PAGE_SHOPS] = storesTabText;
    this.categoryData[CATEGORY_PAGE_OFFERS] = offersTabText;

    if (!window) {
      return;
    }

    this.changeTabBasedOnHash();

    // listen to hash change for changing tabs
    window.addEventListener('hashchange', () => {
      this.changeTabBasedOnHash();
    });
  }

  componentWillUnmount() {
    window.removeEventListener('hashchange', () => {}, false);
  }

  setActiveTab(activeSlug: string) {
    this.setState({ activeTab: activeSlug });

    if (!window) {
      return;
    }
    window.location.hash = `#${activeSlug}`;
  }

  changeTabBasedOnHash() {
    if (!window) {
      return;
    }

    const hashValue: unknown = sanitizeHash(window.location.hash);
    const hashSlug = typeof hashValue === 'string' ? hashValue : '';
    // default to shops tab if the hash is not a valid category hash
    const hashSlugToUse = TABS.includes(hashSlug) ? hashSlug : CATEGORY_PAGE_SHOPS;

    const { activeTab } = this.state;
    if (hashSlugToUse !== activeTab) {
      this.setState({ activeTab: hashSlugToUse });
      window.location.hash = `#${hashSlugToUse}`;
    }
  }

  render() {
    const { activeTab } = this.state;
    const {
      slug,
      name = '',
      heroImageUrl,
      locale,
      userData = {},
      pageStateModifiers = {},
      clearFiltersText = '',
      cancelFilterText = '',
      filterSectionTitleText = '',
      subcategoryFilterTitleText = '',
      applyFilterButtonText = '',
      storesLayoutTitleText = '',
      storesLayoutButtonText = '',
      storesLayoutButtonUrl = '',
      storesLayoutButtonTagSet = {},
      offersLayoutTitleText = '',
      offersLayoutButtonText = '',
      offersLayoutButtonUrl = '',
      offersLayoutButtonTagSet = {},
      subCategories = [],
      layout = [],
      subHeroLayout = [],
      offers = [],
    } = this.props;

    const externalUrl: unknown = makeExternalUrl(heroImageUrl);
    const resolvedHeroImageUrl = typeof externalUrl === 'string' ? externalUrl : '';

    const tabsToRender: Array<{ id: string; label: string }> = (
      TABS.map((tabSlug) => {
        const tabTitle = this.categoryData[tabSlug];

        // short-circuit if no tab data for this tab
        if (!tabTitle) {
          return null;
        }

        return {
          id: tabSlug,
          label: tabTitle,
        };
      }).filter((tab): tab is { id: string; label: string } => tab !== null)
    );

    const renderActiveTab = (mobile = false) => {
      if (activeTab === CATEGORY_PAGE_SHOPS) {
        const favShopIds = Array.isArray(userData.shops)
          ? userData.shops.map((shop: { id?: string }) => shop.id) : [];
        return (
          <LayoutFilterableCollectionView
            favItemIds={favShopIds}
            parentCategoryFilter={name}
            parentSubCategoryNames={this.subCategoriesNames}
            parentSubCategories={subCategories}
            locale={locale}
            pageStateModifiers={pageStateModifiers}
            clearFiltersText={clearFiltersText}
            subcategoryFilterTitleText={subcategoryFilterTitleText}
            cancelFilterText={cancelFilterText}
            applyFilterButtonText={applyFilterButtonText}
            layoutTitleText={storesLayoutTitleText}
            filterSectionTitleText={filterSectionTitleText}
            buttonText={storesLayoutButtonText}
            buttonUrl={storesLayoutButtonUrl}
            tags={storesLayoutButtonTagSet}
            itemType={SHOPS}
            Comp={ShopCard}
            saveItemHandler={saveShop}
            removeItemHandler={removeShopById}
            isMobile={mobile}
            pageContext={`${PAGE_CONTEXT}-stores`}
          />
        );
      }
      if (activeTab === CATEGORY_PAGE_OFFERS) {
        const favOffersIds = Array.isArray(userData.offers)
          ? userData.offers.map((offer: { id?: string }) => offer.id) : [];
        const layoutKey = `category-offers-${slug}`;

        return (
          <MaxWidthLayout key={layoutKey}>
            <LayoutCollectionView
              favItemIds={favOffersIds}
              items={offers}
              Comp={OfferCard}
              itemType={OFFERS}
              viewType={GRID}
              saveItemHandler={saveOffer}
              removeItemHandler={removeOfferById}
              pageStateModifiers={pageStateModifiers}
              locale={locale}
              tags={offersLayoutButtonTagSet}
              buttonText={offersLayoutButtonText}
              buttonUrl={offersLayoutButtonUrl}
              sectionTitle={offersLayoutTitleText}
              showDisplayName
              pageContext={`${PAGE_CONTEXT}-offers`}
            />
          </MaxWidthLayout>
        );
      }
      return null;
    };

    const desktopCategoryHeaderContent = (
      <div>
        <CategoryBannerBackgroundImgDiv hasBottomMargin={subHeroLayout?.length ?? 0}>
          <CategoryBannerBackgroundImg src={resolvedHeroImageUrl} alt="" />
          <CenteredColumnDiv>
            <TextOverlayH1>
              { name }
            </TextOverlayH1>
          </CenteredColumnDiv>
        </CategoryBannerBackgroundImgDiv>
        <RenderLayoutViews key="subHeroLayout" views={subHeroLayout} />
        <MaxWidthLayout>
          <TabGroup
            tabs={tabsToRender}
            activeTab={activeTab}
            onTabChange={(tabSlug: string) => this.setActiveTab(tabSlug)}
            ariaLabel="Category tabs"
            idPrefix="category-tabs"
          />
        </MaxWidthLayout>
      </div>
    );

    const mobileCategoryHeaderContent = (
      <div>
        <CategoryMobileBannerBackgroundImgDiv>
          <CategoryMobileBannerBackgroundImg src={resolvedHeroImageUrl} alt="" />
          <CenteredColumnDiv>
            <TextOverlayH1>
              { name }
            </TextOverlayH1>
          </CenteredColumnDiv>
        </CategoryMobileBannerBackgroundImgDiv>
        <RenderLayoutViews key="subHeroLayout" views={subHeroLayout} />
        <MaxWidthLayout>
          <TabGroup
            tabs={tabsToRender}
            activeTab={activeTab}
            onTabChange={(tabSlug: string) => this.setActiveTab(tabSlug)}
            isMobile
            ariaLabel="Category tabs"
            idPrefix="category-tabs-mobile"
          />
        </MaxWidthLayout>
      </div>
    );

    const renderComp = (
      <div>
        <MediaQueryHelper
          desktopChildren={desktopCategoryHeaderContent}
          mobileChildren={mobileCategoryHeaderContent}
        />
        { renderActiveTab() }
        <RenderLayoutViews key="postTabsLayout" views={layout} />
      </div>
    );
    return renderComp;
  }
}

export default CategoryPage;
